import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Loader2, Paperclip, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { US_STATES } from '../lib/proposalUtils';
import FileUpload from '../components/FileUpload';
import SortableProposalItems from '../components/SortableProposalItems';
import ProposalScopeSection from '../components/ProposalScopeSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProposalFormDialog({ open, onOpenChange, onSave, saving, contacts, editData, onRefresh }) {
  const [proposalTypes, setProposalTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [formType, setFormType] = useState('');
  const [customProposalType, setCustomProposalType] = useState('');
  const [formName, setFormName] = useState('');
  const [formContactFirstName, setFormContactFirstName] = useState('');
  const [formContactLastName, setFormContactLastName] = useState('');
  const [formContactName, setFormContactName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formStreet, setFormStreet] = useState('');
  const [formCity, setFormCity] = useState('');
  const [formState, setFormState] = useState('');
  const [formZip, setFormZip] = useState('');
  const [formServiceStreet, setFormServiceStreet] = useState('');
  const [formServiceCity, setFormServiceCity] = useState('');
  const [formServiceState, setFormServiceState] = useState('');
  const [formServiceZip, setFormServiceZip] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompanyContacts, setSelectedCompanyContacts] = useState([]);
  const [selectedContactIdx, setSelectedContactIdx] = useState('');
  const [formItems, setFormItems] = useState([]);
  const [formNotes, setFormNotes] = useState('');
  const [formValidUntil, setFormValidUntil] = useState('');
  const [formJobTitle, setFormJobTitle] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [customDesc, setCustomDesc] = useState('');
  const [itemQty, setItemQty] = useState('1');
  const [itemPrice, setItemPrice] = useState('0');
  const [itemRateType, setItemRateType] = useState('hour');
  const [attachments, setAttachments] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formApEmail, setFormApEmail] = useState('');
  const [formScopes, setFormScopes] = useState([]);

  // Fetch proposal types and services from API
  useEffect(() => {
    const fetchProposalTypes = async () => {
      try {
        const response = await axios.get(`${API}/proposal-types`);
        setProposalTypes(response.data.filter(t => t.active));
      } catch (error) {
        console.error('Failed to fetch proposal types:', error);
        setProposalTypes([]);
      }
    };
    
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data.filter(s => s.active));
      } catch (error) {
        console.error('Failed to fetch services:', error);
        setServices([]);
      }
    };
    
    fetchProposalTypes();
    fetchServices();
  }, []);

  useEffect(() => {
    const initializeForm = async () => {
      if (editData) {
        setFormType(editData.proposal_type || '');
        setFormName(editData.client_name || '');
        setFormContactFirstName(editData.contact_first_name || editData.contact_name?.split(' ')[0] || '');
        setFormContactLastName(editData.contact_last_name || editData.contact_name?.split(' ').slice(1).join(' ') || '');
        setFormContactName(editData.contact_name || '');
        setFormEmail(editData.client_email || '');
        setFormPhone(editData.client_phone || '');
        setFormStreet(editData.client_street || '');
        setFormCity(editData.client_city || '');
        setFormState(editData.client_state || '');
        setFormZip(editData.client_zip || '');
        setFormServiceStreet(editData.service_address_street || '');
        setFormServiceCity(editData.service_address_city || '');
        setFormServiceState(editData.service_address_state || '');
        setFormServiceZip(editData.service_address_zip || '');
        setFormItems(editData.items || []);
        setFormScopes(editData.scopes || []);
        setFormNotes(editData.notes || '');
        setFormValidUntil(editData.valid_until || '');
        // If valid_until is empty (new proposal from Companies), default to 30 days from today
        if (!editData.valid_until) {
          const defaultValidUntil = new Date();
          defaultValidUntil.setDate(defaultValidUntil.getDate() + 30);
          setFormValidUntil(defaultValidUntil.toISOString().split('T')[0]);
        }
        setFormJobTitle(editData.job_title || '');
        setAttachments(editData.attachments || []);
        setFormApEmail(editData.ar_email_address || '');
        
        // If a company ID is provided (from "New Proposal" button), fetch contacts
        if (editData.prefill_company_id) {
          setSelectedCompanyId(editData.prefill_company_id);
          try {
            const response = await axios.get(`${API}/people?company_id=${editData.prefill_company_id}`);
            const companyContacts = response.data || [];
            setSelectedCompanyContacts(companyContacts);
            
            // If only one contact and no contact info pre-filled, auto-select
            if (companyContacts.length === 1 && !editData.contact_name) {
              setSelectedContactIdx('0');
              const person = companyContacts[0];
              const fullName = person.full_name || person.name || person.display_name || '';
              const nameParts = fullName.split(' ');
              setFormContactFirstName(person.first_name || nameParts[0] || '');
              setFormContactLastName(person.last_name || nameParts.slice(1).join(' ') || '');
              setFormContactName(fullName);
              setFormEmail(person.primary_email || person.email || '');
              setFormPhone(person.primary_phone || person.phone || '');
            } else if (companyContacts.length > 1 && !editData.contact_name) {
              // Multiple contacts - user must select
              setSelectedContactIdx('');
            }
          } catch (error) {
            console.error('Failed to fetch contacts for company:', error);
          }
        }
      } else {
        setFormType('');
        setFormName('');
        setFormContactFirstName('');
        setFormContactLastName('');
        setFormContactName('');
        setFormEmail('');
        setFormPhone('');
        setFormStreet('');
        setFormCity('');
        setFormState('');
        setFormZip('');
        setFormServiceStreet('');
        setFormServiceCity('');
        setFormServiceState('');
        setFormServiceZip('');
        setFormItems([]);
        setFormScopes([]);
        setFormNotes('');
        setFormJobTitle('');
        // Default valid_until to 30 days from today for new proposals
        const defaultValidUntil = new Date();
        defaultValidUntil.setDate(defaultValidUntil.getDate() + 30);
        setFormValidUntil(defaultValidUntil.toISOString().split('T')[0]);
        setAttachments([]);
        setSelectedCompanyId('');
        setSelectedCompanyContacts([]);
        setSelectedContactIdx('');
        setFormApEmail('');
      }
    };
    
    initializeForm();
    setSelectedService('');
    setCustomDesc('');
    setItemQty('1');
    setItemPrice('0');
  }, [editData, open]);

  const addItem = () => {
    // Determine the service name and optional custom description
    let serviceName = '';
    let customDescription = '';
    
    if (selectedService === 'Other') {
      if (!customDesc.trim()) { 
        toast.error('Please enter a custom service description'); 
        return; 
      }
      serviceName = 'Other';
      customDescription = customDesc.trim();
    } else if (selectedService) {
      serviceName = selectedService;
      customDescription = customDesc.trim(); // Allow optional description for any service
    } else {
      toast.error('Please select a service'); 
      return;
    }
    
    const qty = parseFloat(itemQty) || 0;
    const price = parseFloat(itemPrice) || 0;
    const t = qty * price;
    
    const newItem = { 
      service: serviceName,
      description: customDescription || '', // Only show description if explicitly entered
      custom_description: customDescription,
      quantity: qty, 
      unit_price: price, 
      total: t,
      rate_type: itemRateType
    };
    
    if (editingIndex !== null) {
      // Update existing item
      const updatedItems = [...formItems];
      updatedItems[editingIndex] = { ...updatedItems[editingIndex], ...newItem };
      setFormItems(updatedItems);
      setEditingIndex(null);
      toast.success('Service line updated');
    } else {
      // Add new item
      setFormItems([...formItems, newItem]);
    }
    
    // Reset form fields
    setSelectedService('');
    setCustomDesc('');
    setItemQty('1');
    setItemPrice('0');
    setItemRateType('hour');
  };
  
  // Handle editing an existing item - load its data into the form
  const handleEditItem = (item, index) => {
    setSelectedService(item.service || '');
    setCustomDesc(item.custom_description || '');
    setItemQty(item.quantity?.toString() || '1');
    setItemPrice(item.unit_price?.toString() || '0');
    setItemRateType(item.rate_type || 'hour');
    setEditingIndex(index);
    toast.info('Editing service line - make changes and click the button below to save');
  };
  
  // Cancel editing and reset form
  const cancelEdit = () => {
    setSelectedService('');
    setCustomDesc('');
    setItemQty('1');
    setItemPrice('0');
    setItemRateType('hour');
    setEditingIndex(null);
  };

  const removeItem = (i) => {
    setFormItems(formItems.filter((_, idx) => idx !== i));
  };

  const selectContact = async (cid) => {
    const c = contacts.find(x => x.id === cid);
    if (c) {
      setSelectedCompanyId(cid);
      
      // Company/Client name
      setFormName(c.company_name || c.client_name || '');
      
      // AP Email Address from company
      setFormApEmail(c.ar_email_address || '');
      
      // Fetch contacts/people for this company from the People API
      let companyContacts = c.people || c.contacts || [];
      
      // If no embedded contacts, try fetching from People API
      if (companyContacts.length === 0) {
        try {
          const response = await axios.get(`${API}/people?company_id=${cid}`);
          companyContacts = response.data || [];
        } catch (error) {
          console.error('Failed to fetch people for company:', error);
        }
      }
      
      setSelectedCompanyContacts(companyContacts);
      
      // Address for "Bill To" - ONLY use billing address, leave blank if not set
      const street = c.billing_street || '';
      const city = c.billing_city || '';
      const state = c.billing_state || '';
      const zip = c.billing_zip || '';
      
      setFormStreet(street);
      setFormCity(city);
      setFormState(state);
      setFormZip(zip);
      
      // If only one contact, auto-select them
      if (companyContacts.length === 1) {
        setSelectedContactIdx('0');
        selectContactPerson(companyContacts[0]);
      } else if (companyContacts.length > 1) {
        // Clear contact fields so user must select
        setSelectedContactIdx('');
        setFormContactFirstName('');
        setFormContactLastName('');
        setFormContactName('');
        setFormEmail('');
        setFormPhone('');
      } else {
        // No contacts - use company info
        setSelectedContactIdx('');
        setFormEmail(c.email || '');
        setFormPhone(c.phone1 || c.phone || '');
      }
    }
  };

  const selectContactPerson = (person) => {
    if (person) {
      // Handle different name formats from People API vs embedded contacts
      const fullName = person.full_name || person.name || person.display_name || '';
      const nameParts = fullName.split(' ');
      const firstName = person.first_name || nameParts[0] || '';
      const lastName = person.last_name || nameParts.slice(1).join(' ') || '';
      
      setFormContactFirstName(firstName);
      setFormContactLastName(lastName);
      setFormContactName(fullName || `${firstName} ${lastName}`.trim());
      
      // Handle People API format (primary_email, primary_phone) vs embedded format
      setFormEmail(person.primary_email || person.email || '');
      setFormPhone(person.primary_phone || person.phone || person.mobile || '');
    }
  };

  const handleSave = (keepDialogOpen = false) => {
    if (!formType || !formName) { toast.error('Fill required fields'); return; }
    const standaloneTotal = formItems.reduce((a, it) => a + it.total, 0);
    const scopesTotal = formScopes.reduce((a, scope) => 
      a + (scope.items || []).reduce((s, it) => s + (it.total || 0), 0), 0);
    const t = standaloneTotal + scopesTotal;
    onSave({
      proposal_type: formType === 'Other' ? customProposalType : formType,
      job_title: formJobTitle,
      client_name: formName,
      contact_first_name: formContactFirstName,
      contact_last_name: formContactLastName,
      contact_name: `${formContactFirstName} ${formContactLastName}`.trim() || formContactName,
      client_email: formEmail,
      client_phone: formPhone,
      client_street: formStreet,
      client_city: formCity,
      client_state: formState,
      client_zip: formZip,
      ar_email_address: formApEmail,
      service_address_street: formServiceStreet,
      service_address_city: formServiceCity,
      service_address_state: formServiceState,
      service_address_zip: formServiceZip,
      items: formItems,
      scopes: formScopes,
      subtotal: t,
      tax: 0,
      total: t,
      notes: formNotes,
      valid_until: formValidUntil,
      attachments: attachments
    }, editData?.id, keepDialogOpen);
  };

  const standaloneSubtotal = formItems.reduce((s, i) => s + (i.total || 0), 0);
  const scopesSubtotal = formScopes.reduce((a, scope) =>
    a + (scope.items || []).reduce((s, i) => s + (i.total || 0), 0), 0);
  const subtotal = standaloneSubtotal + scopesSubtotal;
  const total = subtotal;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg">{editData ? 'Edit Proposal' : 'Create Proposal'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label>Proposal Type *</Label>
              <Select value={formType} onValueChange={(v) => {
                setFormType(v);
                if (v !== 'Other') setCustomProposalType('');
              }}>
                <SelectTrigger data-testid="proposal-type-select" className="w-full"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {proposalTypes.length > 0 ? (
                    <>
                      {proposalTypes.filter(t => t.name !== 'Other').map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                      <SelectItem value="Other" className="border-t border-slate-200 mt-1 pt-1">
                        <span className="text-slate-600 italic">Other (specify below)</span>
                      </SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="Locate">Locate</SelectItem>
                      <SelectItem value="Soft Dig">Soft Dig</SelectItem>
                      <SelectItem value="CCTV/Flush">CCTV/Flush</SelectItem>
                      <SelectItem value="Other" className="border-t border-slate-200 mt-1 pt-1">
                        <span className="text-slate-600 italic">Other (specify below)</span>
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              {formType === 'Other' && (
                <Input
                  value={customProposalType}
                  onChange={(e) => setCustomProposalType(e.target.value)}
                  placeholder="Describe the proposal type..."
                  className="bg-amber-50 border-amber-300 mt-2"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>Job Title *</Label>
              <Input 
                value={formJobTitle} 
                onChange={(e) => setFormJobTitle(e.target.value)} 
                placeholder="e.g., 123 Main St - Gas Line Survey"
                className="w-full"
                data-testid="proposal-job-title-input"
              />
              <p className="text-xs text-slate-500">This will appear on the proposal and flow through to Job creation</p>
            </div>
            <div className="space-y-2">
              <Label>Valid Until</Label>
              <Input type="date" value={formValidUntil} onChange={(e) => setFormValidUntil(e.target.value)} className="bg-slate-50 w-full" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-base font-semibold">Client Information</Label>
            <div className="grid grid-cols-2 gap-3">
              {contacts.length > 0 && (
                <Select onValueChange={selectContact} value={selectedCompanyId}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Select Company" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {selectedCompanyContacts.length > 0 && (
                <Select 
                  value={selectedContactIdx} 
                  onValueChange={(idx) => {
                    setSelectedContactIdx(idx);
                    selectContactPerson(selectedCompanyContacts[parseInt(idx)]);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Contact Person" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedCompanyContacts.map((p, idx) => (
                      <SelectItem key={p.id || idx} value={idx.toString()}>
                        {p.full_name || p.name || p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Contact ${idx + 1}`}
                        {(p.job_title || p.title) && ` - ${p.job_title || p.title}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {selectedCompanyId && selectedCompanyContacts.length === 0 && (
                <div className="flex items-center text-sm text-slate-500 italic">
                  No contacts for this company
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label>Company/Client Name *</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} className="bg-slate-50" data-testid="proposal-client-name-input" placeholder="Company or individual name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input 
                  value={formContactFirstName} 
                  onChange={(e) => {
                    setFormContactFirstName(e.target.value);
                    setFormContactName(`${e.target.value} ${formContactLastName}`.trim());
                  }} 
                  className="h-10 bg-slate-50" 
                  placeholder="First name" 
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input 
                  value={formContactLastName} 
                  onChange={(e) => {
                    setFormContactLastName(e.target.value);
                    setFormContactName(`${formContactFirstName} ${e.target.value}`.trim());
                  }} 
                  className="h-10 bg-slate-50" 
                  placeholder="Last name" 
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={formPhone} onChange={(e) => setFormPhone(e.target.value)} className="h-10 bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} className="h-10 bg-slate-50" data-testid="proposal-client-email-input" />
            </div>
          </div>

          {/* Client Billing Address */}
          <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 text-blue-800">
              <span className="text-sm font-semibold">📋 Client Billing Address</span>
            </div>
            <p className="text-xs text-blue-600">Where invoices will be sent (from client's CRM profile)</p>
            <div className="space-y-2">
              <Label className="text-xs">Street</Label>
              <Input value={formStreet} onChange={(e) => setFormStreet(e.target.value)} placeholder="123 Main Street or PO Box" className="bg-white" data-testid="proposal-client-street-input" />
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">City</Label>
                <Input value={formCity} onChange={(e) => setFormCity(e.target.value)} className="bg-white" data-testid="proposal-client-city-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">State</Label>
                  <Select value={formState} onValueChange={setFormState}>
                    <SelectTrigger className="bg-white" data-testid="proposal-client-state-select"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">ZIP</Label>
                  <Input value={formZip} onChange={(e) => setFormZip(e.target.value)} className="bg-white" data-testid="proposal-client-zip-input" />
                </div>
              </div>
            </div>
            {/* AP Email Address */}
            <div className="space-y-2 pt-2 border-t border-blue-200">
              <Label className="text-xs">AP Email Address</Label>
              <Input 
                type="email"
                value={formApEmail} 
                onChange={(e) => setFormApEmail(e.target.value)} 
                placeholder="ap@company.com" 
                className="bg-white" 
                data-testid="proposal-ar-email-input" 
              />
              <p className="text-xs text-blue-500">Email for Accounts Payable / invoice communications</p>
            </div>
          </div>

          {/* Service Address (Work Location) */}
          <div className="space-y-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 text-green-800">
              <span className="text-sm font-semibold">📍 Service Address (Work Location)</span>
            </div>
            <p className="text-xs text-green-600">Where the work will be performed</p>
            <div className="space-y-2">
              <Label className="text-xs">Street</Label>
              <Input value={formServiceStreet} onChange={(e) => setFormServiceStreet(e.target.value)} placeholder="123 Job Site Road" className="bg-white" data-testid="proposal-service-street-input" />
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">City</Label>
                <Input value={formServiceCity} onChange={(e) => setFormServiceCity(e.target.value)} className="bg-white" data-testid="proposal-service-city-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">State</Label>
                  <Select value={formServiceState} onValueChange={setFormServiceState}>
                    <SelectTrigger className="bg-white" data-testid="proposal-service-state-select"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">ZIP</Label>
                  <Input value={formServiceZip} onChange={(e) => setFormServiceZip(e.target.value)} className="bg-white" data-testid="proposal-service-zip-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Scopes Section */}
          {formScopes.length > 0 && (
            <div className="space-y-3">
              <Label className="text-base font-semibold">Scopes</Label>
              {formScopes.map((scope, idx) => (
                <ProposalScopeSection
                  key={idx}
                  scope={scope}
                  scopeIndex={idx}
                  services={services}
                  onUpdate={(updatedScope) => {
                    const updated = [...formScopes];
                    updated[idx] = updatedScope;
                    setFormScopes(updated);
                  }}
                  onRemove={(i) => setFormScopes(formScopes.filter((_, si) => si !== i))}
                />
              ))}
            </div>
          )}

          {/* Add Scope Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full border-dashed border-slate-400 text-slate-600 hover:bg-slate-50"
            onClick={() => setFormScopes([...formScopes, { name: '', items: [] }])}
            data-testid="add-scope-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Scope
          </Button>

          <Label className="text-base font-semibold">{formScopes.length > 0 ? 'Additional Items (Outside Scopes)' : 'Service Line Items'}</Label>
          <div className={`space-y-3 p-3 rounded-lg border-2 transition-colors ${editingIndex !== null ? 'border-blue-400 bg-blue-50/50' : 'border-transparent'}`}>
            {editingIndex !== null && (
              <div className="text-sm text-blue-600 font-medium flex items-center gap-1">
                <Pencil className="w-4 h-4" /> Editing Line {editingIndex + 1} - Make changes below
              </div>
            )}
            {/* Row 1: Service dropdown and Description input - SAME LINE like Opportunities */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Service</Label>
                <Select value={selectedService} onValueChange={(value) => {
                  setSelectedService(value);
                  // Auto-populate price based on rate type
                  const service = services.find(s => s.name === value);
                  if (service) {
                    if (itemRateType === 'day' && service.day_price) {
                      setItemPrice(parseFloat(service.day_price) || 0);
                    } else if (itemRateType === 'hour' && service.hourly_price) {
                      setItemPrice(parseFloat(service.hourly_price) || 0);
                    } else if (itemRateType === 'fixed' && service.fixed_price) {
                      setItemPrice(parseFloat(service.fixed_price) || 0);
                    }
                  }
                }}>
                  <SelectTrigger className="bg-slate-50 w-full" data-testid="item-service-select">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => (
                      <SelectItem key={s.id || s.name} value={s.name}>
                        {s.name} {s.day_price ? `($${s.day_price}/day)` : ''} {s.hourly_price ? `($${s.hourly_price}/hr)` : ''} {s.per_hole_price ? `($${s.per_hole_price}/hole)` : ''} {s.fixed_price ? `($${s.fixed_price}/fixed)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{selectedService === 'Other' ? 'Description *' : 'Description'}</Label>
                <Input 
                  value={customDesc} 
                  onChange={(e) => setCustomDesc(e.target.value)} 
                  placeholder={selectedService === 'Other' ? "Enter custom service description" : "Enter description"} 
                  className="bg-slate-50" 
                  data-testid="item-custom-description-input" 
                />
              </div>
            </div>
            {/* Row 2: Rate Type, Qty, Price */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Rate Type</Label>
                {(() => {
                  const currentService = services.find(s => s.name === selectedService);
                  const hasPerHole = currentService?.per_hole_price;
                  return (
                    <Select value={itemRateType} onValueChange={(value) => {
                      setItemRateType(value);
                      // Auto-populate price based on rate type
                      const service = services.find(s => s.name === selectedService);
                      if (service) {
                        if (value === 'day' && service.day_price) {
                          setItemPrice(parseFloat(service.day_price) || 0);
                        } else if (value === 'hour' && service.hourly_price) {
                          setItemPrice(parseFloat(service.hourly_price) || 0);
                        } else if (value === 'hole' && service.per_hole_price) {
                          setItemPrice(parseFloat(service.per_hole_price) || 0);
                        } else if (value === 'fixed' && service.fixed_price) {
                          setItemPrice(parseFloat(service.fixed_price) || 0);
                        } else if (value === 'night' && service.night_price) {
                          setItemPrice(parseFloat(service.night_price) || 0);
                        }
                      }
                    }}>
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hour">Per Hour</SelectItem>
                        <SelectItem value="day">Per Day</SelectItem>
                        <SelectItem value="night">Per Night</SelectItem>
                        {hasPerHole && <SelectItem value="hole">Per Hole</SelectItem>}
                        <SelectItem value="linear_footage">Linear Footage</SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Qty ({itemRateType === 'hour' ? 'hrs' : itemRateType === 'day' ? 'days' : itemRateType === 'night' ? 'nights' : itemRateType === 'hole' ? 'holes' : itemRateType === 'linear_footage' ? 'LF' : 'units'})</Label>
                <Input type="number" min="0" step="0.5" value={itemQty} onChange={(e) => setItemQty(e.target.value)} className="bg-slate-50" data-testid="item-quantity-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Price ($/{itemRateType === 'hour' ? 'hr' : itemRateType === 'day' ? 'day' : itemRateType === 'night' ? 'night' : itemRateType === 'hole' ? 'hole' : itemRateType === 'linear_footage' ? 'LF' : 'unit'})</Label>
                <Input type="number" step="0.01" value={itemPrice} onChange={(e) => setItemPrice(e.target.value)} className="bg-slate-50" data-testid="item-price-input" />
              </div>
            </div>
          </div>

          {formItems.length > 0 && (
            <SortableProposalItems
              items={formItems}
              onItemsChange={setFormItems}
              onEdit={handleEditItem}
            />
          )}

          {/* Add/Update Service Line Button - at bottom of list */}
          {editingIndex !== null ? (
            <div className="flex gap-2">
              <Button onClick={addItem} variant="default" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="update-item-btn">
                <Pencil className="w-4 h-4 mr-1" /> Update Service Line
              </Button>
              <Button onClick={cancelEdit} variant="outline" className="text-slate-600" data-testid="cancel-edit-btn">
                Cancel
              </Button>
            </div>
          ) : (
            <Button onClick={addItem} variant="outline" className="w-full" data-testid="add-item-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Service Line
            </Button>
          )}

          <div className="bg-slate-50 rounded-lg p-3 sm:p-4 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-600">Subtotal</span><span className="font-medium">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2"><span>Total</span><span>${total.toFixed(2)}</span></div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} rows={4} className="bg-slate-50 min-h-[100px] resize-y" placeholder="Additional notes, terms, or special instructions..." />
          </div>

          {/* File Attachments Section */}
          {editData ? (
            <FileUpload
              files={attachments}
              uploadEndpoint={`/api/proposals/${editData.id}/files`}
              deleteEndpoint={`/api/proposals/${editData.id}/files`}
              onUploadSuccess={async () => {
                try {
                  const response = await axios.get(`${API}/proposals/${editData.id}`);
                  setAttachments(response.data.attachments || []);
                  if (onRefresh) onRefresh();
                } catch (error) {
                  console.error('Failed to refresh:', error);
                }
              }}
              onDeleteSuccess={async () => {
                try {
                  const response = await axios.get(`${API}/proposals/${editData.id}`);
                  setAttachments(response.data.attachments || []);
                  if (onRefresh) onRefresh();
                } catch (error) {
                  console.error('Failed to refresh:', error);
                }
              }}
              title="Attachments"
              emptyMessage="Drag & drop files here, or click to upload. Site plans, photos, documents."
            />
          ) : (
            <div className="p-4 bg-amber-50 border border-dashed border-amber-300 rounded-lg text-center">
              <Paperclip className="w-8 h-8 mx-auto text-amber-500 mb-2" />
              <p className="text-sm text-amber-700 font-medium">File Attachments</p>
              <p className="text-xs text-amber-600 mt-1">
                Click "Save & Add Files" below to save and enable file uploads.
              </p>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {!editData && (
              <Button 
                onClick={() => handleSave(true)} 
                disabled={saving} 
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                <Paperclip className="w-4 h-4 mr-2" />
                Save & Add Files
              </Button>
            )}
            <Button onClick={() => handleSave(false)} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="save-proposal-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editData ? 'Update' : 'Create'} Proposal
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
